import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Car, Accessibility, Navigation, Mail, Info } from 'lucide-react';

const Location: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'info' | 'hours'>('info');

  const schedule = [
    { day: t('monday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' },
    { day: t('tuesday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' },
    { day: t('wednesday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' },
    { day: t('thursday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' },
    { day: t('friday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' },
    { day: t('saturday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' },
    { day: t('sunday'), lunch: '12h00 - 14h30', dinner: '19h00 - 22h30' }
  ];

  return (
    <div className="relative h-screen">
      {/* Map Background - Full Screen */}
      <div className="absolute inset-0 z-0">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2565.3058558503!2d4.0201!3d49.2458!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0:0x0!2s18%20Bd%20d'Alsace%20Lorraine%2C%2051350%20Cormontreuil!5e0!3m2!1sfr!2sfr!4v1703268900000!5m2!1sfr!2sfr"
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'brightness(0.9)' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Carnival Restaurant Location"
        />
      </div>

      {/* Header Bar */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/70 to-transparent pt-24 pb-8">
        <div className="section-padding">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-display text-white text-center mb-2"
          >
            CARNIVAL RESTAURANT
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white/80 text-center text-lg"
          >
            18 Boulevard d'Alsace Lorraine, 51350 Cormontreuil
          </motion.p>
        </div>
      </div>

      {/* Left Side Contact Card */}
      <motion.div 
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-full md:w-auto md:max-w-md"
      >
        <div className="bg-white/95 backdrop-blur-sm m-4 md:m-8 rounded-2xl shadow-2xl overflow-hidden">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('info')}
              className={`flex-1 py-4 px-6 font-display text-lg transition-colors ${
                activeTab === 'info' 
                  ? 'bg-carnival-burgundy text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t('information')}
            </button>
            <button
              onClick={() => setActiveTab('hours')}
              className={`flex-1 py-4 px-6 font-display text-lg transition-colors ${
                activeTab === 'hours' 
                  ? 'bg-carnival-burgundy text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t('hours')}
            </button>
          </div>

          <div className="p-6 md:p-8">
            {activeTab === 'info' ? (
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-gray-800 mb-1">{t('address')}</h3>
                    <p className="text-gray-600">18 Boulevard d'Alsace Lorraine</p>
                    <p className="text-gray-600">51350 Cormontreuil</p>
                    <p className="text-sm text-gray-500 mt-1">{t('insideMall')}</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-gray-800 mb-1">{t('phone')}</h3>
                    <a href="tel:0326502026" className="text-carnival-burgundy hover:text-carnival-wine transition-colors text-lg font-semibold">
                      03 26 50 20 26
                    </a>
                    <p className="text-sm text-gray-500 mt-1">{t('noReservation')}</p>
                  </div>
                </div>

                {/* Parking */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Car className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-gray-800 mb-1">{t('parking')}</h3>
                    <p className="text-gray-600">{t('freeParking')}</p>
                    <p className="text-sm text-gray-500">{t('outdoorParking')}</p>
                  </div>
                </div>

                {/* Accessibility */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Accessibility className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-gray-800 mb-1">{t('access')}</h3>
                    <p className="text-gray-600">{t('wheelchairAccessible')}</p>
                  </div>
                </div>

                {/* Directions Buttons */}
                <div className="pt-4 space-y-3">
                  <a
                    href="https://www.google.com/maps/dir//18+Bd+d'Alsace+Lorraine,+51350+Cormontreuil"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-black text-white py-4 px-6 rounded-lg font-display text-center block hover:bg-gray-800 transition-colors"
                  >
                    <Navigation className="inline-block mr-2" size={20} />
                    {t('getDirections')}
                  </a>
                  <a
                    href="tel:0326502026"
                    className="w-full bg-carnival-burgundy text-white py-4 px-6 rounded-lg font-display text-center block hover:bg-carnival-wine transition-colors"
                  >
                    <Phone className="inline-block mr-2" size={20} />
                    {t('callNow')}
                  </a>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h3 className="font-display text-xl text-gray-800 mb-2 flex items-center gap-2">
                    <Clock size={24} />
                    {t('openingHours')}
                  </h3>
                  <p className="text-carnival-burgundy font-semibold">{t('openSevenDays')}</p>
                </div>

                <div className="space-y-3">
                  {schedule.map((item, index) => (
                    <div
                      key={index}
                      className={`grid grid-cols-3 py-3 px-4 rounded-lg ${
                        index % 2 === 0 ? 'bg-gray-50' : ''
                      }`}
                    >
                      <div className="font-semibold text-gray-800">{item.day}</div>
                      <div className="text-center text-gray-600 text-sm">{item.lunch}</div>
                      <div className="text-center text-gray-600 text-sm">{item.dinner}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <Info className="inline-block mr-2" size={16} />
                    {t('kitchenClosesInfo')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Right Side Quick Info Cards */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="absolute right-4 md:right-8 bottom-8 z-20 space-y-4 hidden lg:block"
      >
        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
          <p className="font-display text-lg text-gray-800 mb-1">{t('buffet')}</p>
          <p className="text-2xl font-bold text-carnival-burgundy">19.90€ - 25.90€</p>
        </div>
        
        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
          <p className="font-display text-lg text-gray-800 mb-1">{t('capacity')}</p>
          <p className="text-2xl font-bold text-black">400+ {t('seats')}</p>
        </div>
      </motion.div>

      {/* Mobile Bottom Sheet */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white rounded-t-2xl shadow-2xl">
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4" />
        <div className="px-6 pb-6">
          <h2 className="font-display text-xl mb-3">CARNIVAL RESTAURANT</h2>
          <p className="text-gray-600 mb-4">18 Bd d'Alsace Lorraine, 51350 Cormontreuil</p>
          <div className="flex gap-3">
            <a
              href="https://www.google.com/maps/dir//18+Bd+d'Alsace+Lorraine,+51350+Cormontreuil"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-black text-white py-3 px-4 rounded-lg font-display text-center text-sm"
            >
              {t('directions')}
            </a>
            <a
              href="tel:0326502026"
              className="flex-1 bg-carnival-burgundy text-white py-3 px-4 rounded-lg font-display text-center text-sm"
            >
              {t('call')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Location;